import cn from '@/utils/cn';
import React, { useRef, useState } from 'react';
import Text from '@/components/ui/Text';
import styles from './PlanningDrawer.module.scss';
import { Resource, TRANSLATIONS } from './utils';

interface TabbedContentProps {
  resources: Resource[];
}

const tabId = (resourceId: number) => `planning-tab-${resourceId}`;
const panelId = (resourceId: number) => `planning-panel-${resourceId}`;

export const TabbedContent = ({ resources }: TabbedContentProps) => {
  const [id, setId] = useState(resources[0].id);
  const tabRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let nextIndex: number | null = null;

    if (e.key === 'ArrowDown') nextIndex = (index + 1) % resources.length;
    else if (e.key === 'ArrowUp') nextIndex = (index - 1 + resources.length) % resources.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = resources.length - 1;

    if (nextIndex !== null) {
      e.preventDefault();
      const nextResource = resources[nextIndex];
      setId(nextResource.id);
      tabRefs.current[nextResource.id]?.focus();
    }
  };

  return (
    <div className="tw-flex tw-py-8 tw-px-4 tw-overflow-hidden">
      <ul
        aria-orientation="vertical"
        className={`tw-flex tw-flex-col tw-border-r tw-border-solid tw-border-med-grey tw-flex-1 ${styles.tablist}`}
        role="tablist"
      >
        {resources.map((resource, index) => {
          const isSelected = id === resource.id;

          return (
            <li key={resource.id} className="tw-w-full tw-pr-4 tw-box-border" role="presentation">
              <button
                ref={(el) => { tabRefs.current[resource.id] = el; }}
                aria-controls={panelId(resource.id)}
                aria-selected={isSelected}
                className={cn(
                  'tw-w-full tw-border-none tw-py-4 tw-bg-white tw-pl-2 tw-text-dark-grey tw-outline-none',
                  'tw-border-0 tw-border-l-4 tw-border-transparent tw-border-solid tw-box-border',
                  'hover:tw-text-tci-purple hover:tw-border-l-tci-purple',
                  'focus-visible:tw-text-tci-purple focus-visible:tw-border-l-tci-purple',
                  'focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple',
                  'focus-visible:tw-outline-offset-[-2px]',
                  'focus:tw-outline-none',
                  'tw-text-left tw-transition-all tw-cursor-pointer tw-text-base',
                  { 'tw-text-tci-purple tw-border-l-tci-purple': isSelected },
                )}
                id={tabId(resource.id)}
                role="tab"
                tabIndex={isSelected ? 0 : -1}
                type="button"
                onClick={() => setId(resource.id)}
                onKeyDown={e => handleKeyDown(e, index)}
              >
                {resource.title}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="fr-box fr-basic tw-w-4/5 tw-border-none">
        <div className="fr-wrapper tw-overflow-x-hidden !tw-border-none">
          {resources.map((resource) => {
            const isSelected = resource.id === id;
            // AIDEV-NOTE: matches model_resource_templates.name (template 5) — renaming that template drops
            // this heading and the container class. Server-side twin is ModelResource#materials?.
            const isMaterials = resource.name === 'Materials';

            return (
              <div
                key={resource.id}
                aria-hidden={!isSelected}
                aria-labelledby={tabId(resource.id)}
                className={cn(
                  {
                    'tw-hidden !tw-h-0 tw-overflow-hidden': !isSelected,
                    'tw-pb-2': isSelected,
                  },
                  'tw-pl-4 fr-element fr-view tw-m-0',
                )}
                id={panelId(resource.id)}
                role="tabpanel"
                tabIndex={isSelected ? 0 : -1}
              >
                {isMaterials && Boolean(resource.html)
                  && <Text size="xl" tag="h2" variant="label">{TRANSLATIONS.materials}</Text>}
                <div
                  className={cn({ 'model-resource-container': isMaterials })}
                  dangerouslySetInnerHTML={{ __html: resource.html ?? '' }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
